import React, { useState, useEffect, useCallback } from "react";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import adminFetch from "../../universal/adminFetch";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const GEO_QUEUES = [
  { id: "eume", label: "Europe & Middle East" },
  { id: "na", label: "North America" },
  { id: "sa", label: "South America" },
  { id: "apac", label: "Asia-Pacific" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "verified", label: "Verified players only" },
  { value: "pending", label: "Pending players (pending + pending review)" },
  { value: "all", label: "All players (verified + pending)" },
];

const CAMPAIGN_STATUS_COLORS = {
  scheduled: "#f59e0b",
  sending: "#3b82f6",
  sent: "#10b981",
  partial_failed: "#f97316",
  failed: "#ef4444",
  cancelled: "#6b7280",
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: "0 auto",
  },
  pageTitle: {
    fontSize: "1.6rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  pageSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 12,
  },
  sectionTitle: {
    fontSize: "1rem",
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  flexGrow: {
    flex: 1,
    minWidth: 200,
  },
  templateCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "border-color 0.15s ease",
    "&:hover": {
      borderColor: "#3b82f6",
    },
  },
  templateCardSelected: {
    borderColor: "#3b82f6",
    backgroundColor: "rgba(59, 130, 246, 0.04)",
  },
  templateName: {
    fontWeight: 600,
    fontSize: "0.9rem",
  },
  templateSubject: {
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
  editTemplateBtn: {
    marginLeft: theme.spacing(1),
    fontSize: "0.75rem",
  },
  previewBox: {
    background: theme.palette.background.default,
    borderRadius: 8,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
  },
  previewCount: {
    fontSize: "2rem",
    fontWeight: 700,
    color: "#3b82f6",
    lineHeight: 1,
  },
  previewLabel: {
    fontSize: "0.85rem",
    color: theme.palette.text.secondary,
  },
  breakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "0.85rem",
    padding: "4px 0",
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
  },
  campaignRow: {
    padding: theme.spacing(1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    "&:last-child": { borderBottom: "none" },
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  campaignMeta: {
    flex: 1,
    minWidth: 200,
  },
  campaignSubject: {
    fontWeight: 500,
    fontSize: "0.9rem",
  },
  campaignDetails: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  statusChip: {
    fontSize: "0.72rem",
    height: 22,
  },
  htmlPreview: {
    fontFamily: "inherit",
    fontSize: "0.85rem",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    padding: theme.spacing(1.5),
    background: theme.palette.background.default,
    maxHeight: 300,
    overflowY: "auto",
    "& a": { color: "#3b82f6" },
  },
}));

function formatMs(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString();
}

function StatusChip({ status, classes }) {
  const color = CAMPAIGN_STATUS_COLORS[status] || "#6b7280";
  return (
    <Chip
      label={status}
      size="small"
      className={classes.statusChip}
      style={{ backgroundColor: color, color: "#fff" }}
    />
  );
}

function TemplateEditorDialog({ open, template, onClose, onSave }) {
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && template) {
      setName(template.name || "");
      setSubject(template.subject || "");
      setBodyHtml(template.bodyHtml || "");
      setError("");
      setSaving(false);
    } else if (open && !template) {
      setName("");
      setSubject("");
      setBodyHtml("");
      setError("");
      setSaving(false);
    }
  }, [open, template]);

  const handleSave = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      setError("All fields are required");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSave({ name, subject, bodyHtml, templateId: template?._id });
      onClose();
    } catch (err) {
      setError(err.message || "Failed to save template");
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{template ? "Edit Template" : "New Template"}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 12 }}>
            {error}
          </Alert>
        )}
        <TextField
          fullWidth
          label="Template name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
          size="small"
          style={{ marginBottom: 12 }}
        />
        <TextField
          fullWidth
          label="Email subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          variant="outlined"
          size="small"
          style={{ marginBottom: 12 }}
        />
        <TextField
          fullWidth
          label="Email body (HTML supported)"
          value={bodyHtml}
          onChange={(e) => setBodyHtml(e.target.value)}
          variant="outlined"
          multiline
          minRows={10}
          maxRows={20}
          style={{ marginBottom: 12 }}
          helperText="You can use basic HTML tags: <strong>, <br />, <a href='...'>, <small>. This content is injected into the standard Ouro email template."
        />
        {bodyHtml && (
          <>
            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
              Preview:
            </Typography>
            <iframe
              title="template-preview"
              sandbox=""
              srcDoc={`<div style="font-family: Arial, sans-serif; font-size: 14px; line-height: 1.5;">${bodyHtml}</div>`}
              style={{
                width: "100%",
                minHeight: 250,
                border: "1px solid #e0e0e0",
                borderRadius: 6,
                background: "#fafafa",
              }}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function EmailCampaignsPage({ userId }) {
  const classes = useStyles();

  const [templates, setTemplates] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Compose form state
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [subjectOverride, setSubjectOverride] = useState("");
  const [bodyOverride, setBodyOverride] = useState("");
  const [geoQueueIds, setGeoQueueIds] = useState(["eume"]);
  const [statusFilter, setStatusFilter] = useState("verified");
  const [allUsersWithRealEmail, setAllUsersWithRealEmail] = useState(false);
  const [scheduledFor, setScheduledFor] = useState(""); // datetime-local value
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [testSendLoading, setTestSendLoading] = useState(false);
  const [testSendSuccess, setTestSendSuccess] = useState("");

  // Template editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTemplate, setEditorTemplate] = useState(null);

  const loadData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const [tplRes, campRes] = await Promise.all([
        adminFetch(`${basePath}/api/v7/admin/email-campaigns/templates/adpass/${userId}`),
        adminFetch(`${basePath}/api/v7/admin/email-campaigns/adpass/${userId}`),
      ]);
      const tplData = await tplRes.json().catch(() => ({}));
      const campData = await campRes.json().catch(() => ({}));
      if (!tplRes.ok || !tplData.ok)
        throw new Error(tplData.err || tplData.error || "Failed to load templates");
      if (!campRes.ok || !campData.ok)
        throw new Error(campData.err || campData.error || "Failed to load campaigns");
      setTemplates(tplData.templates || []);
      setCampaigns(campData.campaigns || []);
      if (!selectedTemplateId && tplData.templates?.length > 0) {
        setSelectedTemplateId(tplData.templates[0]._id);
      }
    } catch (err) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleGeoToggle = (id) => {
    if (allUsersWithRealEmail) return;
    setGeoQueueIds((prev) =>
      prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id]
    );
    setPreview(null);
  };

  const handleAllUsersWithRealEmailToggle = (event) => {
    setAllUsersWithRealEmail(event.target.checked);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!userId) return;
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/preview/adpass/${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            geoQueueIds,
            statusFilter,
            allUsersWithRealEmail,
            templateId: selectedTemplateId || undefined,
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Preview failed");
      setPreview(data);
    } catch (err) {
      setSendError(err.message || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!userId) return;
    if (!selectedTemplateId && (!subjectOverride.trim() || !bodyOverride.trim())) {
      setSendError("Select a template or enter a subject and body");
      return;
    }
    if (!allUsersWithRealEmail && geoQueueIds.length === 0) {
      setSendError("Select at least one region");
      return;
    }

    setSendLoading(true);
    setSendError("");
    setSendSuccess("");

    const scheduledForMs = scheduledFor
      ? new Date(scheduledFor).getTime()
      : null;
    if (scheduledFor && Number.isNaN(scheduledForMs)) {
      setSendLoading(false);
      setSendError("Enter a valid future schedule time");
      return;
    }

    try {
      const body = {
        geoQueueIds,
        statusFilter,
        allUsersWithRealEmail,
        scheduledForMs,
      };

      if (selectedTemplateId) {
        body.templateId = selectedTemplateId;
        if (subjectOverride.trim()) body.subject = subjectOverride.trim();
        if (bodyOverride.trim()) body.bodyHtml = bodyOverride.trim();
      } else {
        body.subject = subjectOverride.trim();
        body.bodyHtml = bodyOverride.trim();
      }

      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/adpass/${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Failed to create campaign");

      const camp = data.campaign;
      const when =
        camp.scheduledForMs && camp.scheduledForMs > Date.now()
          ? `Scheduled for ${formatMs(camp.scheduledForMs)}`
          : "Sending now";
      setSendSuccess(`Campaign created. ${when}.`);
      setScheduledFor("");
      setPreview(null);
      loadData();
    } catch (err) {
      setSendError(err.message || "Failed to send");
    } finally {
      setSendLoading(false);
    }
  };

  const handleTestSend = async () => {
    if (!userId) return;
    if (!testEmail.trim()) {
      setSendError("Enter a test email address");
      return;
    }
    if (!selectedTemplateId && (!subjectOverride.trim() || !bodyOverride.trim())) {
      setSendError("Select a template or enter a subject and body before sending a test");
      return;
    }

    setTestSendLoading(true);
    setSendError("");
    setTestSendSuccess("");

    try {
      const body = {
        testEmail: testEmail.trim(),
      };

      if (selectedTemplateId) {
        body.templateId = selectedTemplateId;
        if (subjectOverride.trim()) body.subject = subjectOverride.trim();
        if (bodyOverride.trim()) body.bodyHtml = bodyOverride.trim();
      } else {
        body.subject = subjectOverride.trim();
        body.bodyHtml = bodyOverride.trim();
      }

      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/test-send/adpass/${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.err || data.error || "Failed to send test email");
      }

      setTestSendSuccess(`Test email sent to ${data.sentTo}.`);
    } catch (err) {
      setSendError(err.message || "Failed to send test email");
    } finally {
      setTestSendLoading(false);
    }
  };

  const handleCancelCampaign = async (campaignId) => {
    if (!userId) return;
    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/${campaignId}/cancel/adpass/${userId}`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Cancel failed");
      loadData();
    } catch (err) {
      setError(err.message || "Cancel failed");
    }
  };

  const handleMarkCampaignSent = async (campaignId) => {
    if (!userId) return;
    const confirmed = window.confirm(
      "Mark this campaign as sent? This is a fail-safe action for campaigns that already reached the mail provider."
    );
    if (!confirmed) return;

    try {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/${campaignId}/mark-sent/adpass/${userId}`,
        { method: "POST", headers: { "Content-Type": "application/json" } }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Mark sent failed");
      loadData();
    } catch (err) {
      setError(err.message || "Mark sent failed");
    }
  };

  const handleSaveTemplate = async ({ templateId, name, subject, bodyHtml }) => {
    if (templateId) {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/templates/${templateId}/adpass/${userId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, subject, bodyHtml }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Failed to update template");
    } else {
      const res = await adminFetch(
        `${basePath}/api/v7/admin/email-campaigns/templates/adpass/${userId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, subject, bodyHtml }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok)
        throw new Error(data.err || data.error || "Failed to create template");
    }
    await loadData();
  };

  const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography className={classes.pageTitle}>Email Campaigns</Typography>
      <Typography className={classes.pageSubtitle}>
        Send or schedule broadcast emails to players by region and account status.
      </Typography>

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        {/* LEFT COLUMN: Compose */}
        <div style={{ flex: 2, minWidth: 340 }}>

          {/* 1. Templates */}
          <Paper className={classes.section}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                1. Choose a template
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() => { setEditorTemplate(null); setEditorOpen(true); }}
              >
                + New template
              </Button>
            </div>

            {templates.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No templates yet. Create one above or run{" "}
                <code>npm run seed:email-templates</code> on the edge server.
              </Typography>
            )}

            {templates.map((t) => (
              <div
                key={t._id}
                className={`${classes.templateCard} ${selectedTemplateId === t._id ? classes.templateCardSelected : ""}`}
                onClick={() => {
                  setSelectedTemplateId(t._id);
                  setSubjectOverride("");
                  setBodyOverride("");
                  setPreview(null);
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <Typography className={classes.templateName}>{t.name}</Typography>
                    <Typography className={classes.templateSubject}>{t.subject}</Typography>
                  </div>
                  <Button
                    size="small"
                    className={classes.editTemplateBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditorTemplate(t);
                      setEditorOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}

            {selectedTemplate && (
              <div style={{ marginTop: 12 }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Override subject (optional):
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={selectedTemplate.subject}
                  value={subjectOverride}
                  onChange={(e) => setSubjectOverride(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Override body (optional, HTML):
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  multiline
                  minRows={4}
                  maxRows={12}
                  placeholder="Leave empty to use template body as-is"
                  value={bodyOverride}
                  onChange={(e) => setBodyOverride(e.target.value)}
                />
              </div>
            )}

            {!selectedTemplateId && (
              <div style={{ marginTop: 12 }}>
                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Or compose from scratch — Subject:
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={subjectOverride}
                  onChange={(e) => setSubjectOverride(e.target.value)}
                  style={{ marginBottom: 10 }}
                />
                <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Body (HTML):
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  multiline
                  minRows={6}
                  maxRows={16}
                  value={bodyOverride}
                  onChange={(e) => setBodyOverride(e.target.value)}
                />
              </div>
            )}
          </Paper>

          {/* 2. Recipients */}
          <Paper className={classes.section}>
            <Typography className={classes.sectionTitle}>2. Recipients</Typography>

            <FormControlLabel
              control={
                <Checkbox
                  checked={allUsersWithRealEmail}
                  onChange={handleAllUsersWithRealEmailToggle}
                  color="primary"
                  size="small"
                />
              }
              label="Send to all users with a real email"
            />
            <Typography variant="caption" style={{ display: "block", marginBottom: 12, color: "#6b7280" }}>
              Ignores regions and excludes Steam/PlaySafe placeholder emails. Account status still applies.
            </Typography>

            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
              Regions:
            </Typography>
            <FormGroup row style={{ marginBottom: 12 }}>
              {GEO_QUEUES.map((g) => (
                <FormControlLabel
                  key={g.id}
                  control={
                    <Checkbox
                      checked={geoQueueIds.includes(g.id)}
                      onChange={() => handleGeoToggle(g.id)}
                      color="primary"
                      size="small"
                      disabled={allUsersWithRealEmail}
                    />
                  }
                  label={g.label}
                />
              ))}
            </FormGroup>

            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
              <InputLabel>Account status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPreview(null); }}
                label="Account status"
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              onClick={handlePreview}
              disabled={previewLoading || (!allUsersWithRealEmail && geoQueueIds.length === 0)}
            >
              {previewLoading ? <CircularProgress size={14} /> : "Preview recipient count"}
            </Button>

            {preview && (
              <div className={classes.previewBox} style={{ marginTop: 12 }}>
                <Typography className={classes.previewCount}>{preview.total}</Typography>
                <Typography className={classes.previewLabel}>
                  recipients will receive this email
                </Typography>
                {preview.breakdown && (
                  <div style={{ marginTop: 8 }}>
                    {Object.entries(preview.breakdown).map(([geoId, info]) => (
                      <div key={geoId} className={classes.breakdownRow}>
                        <span>{info.name}</span>
                        <span style={{ fontWeight: 600 }}>{info.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Paper>

          {/* 3. Schedule and Send */}
          <Paper className={classes.section}>
            <Typography className={classes.sectionTitle}>3. Send</Typography>

            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
              Test send to a single address:
            </Typography>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
              <TextField
                type="email"
                variant="outlined"
                size="small"
                placeholder="you@example.com"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                style={{ minWidth: 260, flex: 1 }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={handleTestSend}
                disabled={testSendLoading}
              >
                {testSendLoading ? "Sending test..." : "Send test email"}
              </Button>
            </div>

            {testSendSuccess && (
              <Alert severity="success" style={{ marginBottom: 12 }}>
                {testSendSuccess}
              </Alert>
            )}

            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
              Test send only goes to the explicit address above. It does not create a campaign and does not use the recipient filters below.
            </Typography>

            <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>
              Schedule for later (leave empty to send immediately):
            </Typography>
            <TextField
              type="datetime-local"
              variant="outlined"
              size="small"
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
              style={{ marginBottom: 16 }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().slice(0, 16) }}
            />

            {sendSuccess && (
              <Alert severity="success" style={{ marginBottom: 12 }}>
                {sendSuccess}
              </Alert>
            )}
            {sendError && (
              <Alert severity="error" style={{ marginBottom: 12 }} onClose={() => setSendError("")}>
                {sendError}
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={handleSend}
              disabled={sendLoading || (!allUsersWithRealEmail && geoQueueIds.length === 0)}
              fullWidth
            >
              {sendLoading
                ? "Creating campaign..."
                : scheduledFor
                  ? `Schedule for ${new Date(scheduledFor).toLocaleString()}`
                  : "Send now"}
            </Button>
          </Paper>
        </div>

        {/* RIGHT COLUMN: Campaign history */}
        <div style={{ flex: 1.2, minWidth: 300 }}>
          <Paper className={classes.section}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 0 }}>
                Campaign history
              </Typography>
              <Button size="small" onClick={loadData}>
                Refresh
              </Button>
            </div>

            {campaigns.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No campaigns yet.
              </Typography>
            )}

            {campaigns.map((c) => (
              <div key={c._id} className={classes.campaignRow}>
                <div className={classes.campaignMeta}>
                  <Typography className={classes.campaignSubject}>
                    {c.templateSnapshot?.subject || "—"}
                  </Typography>
                  <Typography className={classes.campaignDetails}>
                    {c.filters?.allUsersWithRealEmail
                      ? "all real-email users"
                      : c.filters?.geoQueueIds?.join(", ")} · {c.filters?.statusFilter}
                    {c.scheduledForMs ? ` · Scheduled: ${formatMs(c.scheduledForMs)}` : ""}
                    {c.status === "sent" || c.status === "partial_failed"
                      ? ` · sent ${c.sentCount}/${c.totalRecipients}`
                      : ""}
                    {c.createdAtMs ? ` · Created: ${formatMs(c.createdAtMs)}` : ""}
                  </Typography>
                </div>
                <StatusChip status={c.status} classes={classes} />
                {c.status === "scheduled" && (
                  <Button
                    size="small"
                    color="secondary"
                    onClick={() => handleCancelCampaign(c._id)}
                  >
                    Cancel
                  </Button>
                )}
                {c.status !== "sent" && c.status !== "cancelled" && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleMarkCampaignSent(c._id)}
                  >
                    Mark sent
                  </Button>
                )}
              </div>
            ))}
          </Paper>
        </div>
      </div>

      {/* Template Editor Dialog */}
      <TemplateEditorDialog
        open={editorOpen}
        template={editorTemplate}
        onClose={() => setEditorOpen(false)}
        onSave={handleSaveTemplate}
      />
    </div>
  );
}
